// Prize catalogue. Illustrations are clean line-art in `currentColor` (so they
// inherit the theme's text colour) with a single honey accent — premium and
// restrained rather than heavily yellow.

import React from 'react';

export const ApartmentIllustration = ({ className }) => (
  <svg
    viewBox="0 0 96 96"
    className={className}
    role="img"
    aria-hidden="true"
    focusable="false"
    fill="none"
  >
    <path
      d="M20 46 48 22l28 24"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M26 42v34h44V42"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinejoin="round"
    />
    <rect x="42" y="58" width="12" height="18" rx="1.5" stroke="currentColor" strokeWidth="1.6" opacity="0.6" />
    <rect x="32" y="50" width="9" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.4" opacity="0.5" />
    <rect x="55" y="50" width="9" height="9" rx="1.2" stroke="currentColor" strokeWidth="1.4" opacity="0.5" />
    <path
      d="M70 15l1.6 3.9 3.9 1.6-3.9 1.6L70 27.6l-1.6-3.9-3.9-1.6 3.9-1.6L70 15z"
      fill="var(--rf-accent)"
    />
  </svg>
);

export const CheckupIllustration = ({ className }) => (
  <svg
    viewBox="0 0 96 96"
    className={className}
    role="img"
    aria-hidden="true"
    focusable="false"
    fill="none"
  >
    <rect x="18" y="18" width="60" height="60" rx="10" stroke="currentColor" strokeWidth="2.4" />
    <path
      d="M28 50h9l5-13 8 26 6-19h12"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      opacity="0.75"
    />
    <path
      d="M62 58l6 6 10-12"
      stroke="var(--rf-accent)"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export const SofaIllustration = ({ className }) => (
  <svg
    viewBox="0 0 96 96"
    className={className}
    role="img"
    aria-hidden="true"
    focusable="false"
    fill="none"
  >
    <path
      d="M26 44v-8a6 6 0 0 1 6-6h32a6 6 0 0 1 6 6v8"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
    />
    <rect x="18" y="44" width="60" height="20" rx="6" stroke="currentColor" strokeWidth="2.4" />
    <path
      d="M22 60v10a4 4 0 0 0 4 4h4a4 4 0 0 0 4-4v-6M62 64v6a4 4 0 0 0 4 4h4a4 4 0 0 0 4-4V60"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.6"
    />
    <path
      d="M30 51h12v10H30zM54 51h12v10H54z"
      stroke="currentColor"
      strokeWidth="1.5"
      opacity="0.4"
    />
    <path
      d="M70 15l1.6 3.9 3.9 1.6-3.9 1.6L70 27.6l-1.6-3.9-3.9-1.6 3.9-1.6L70 15z"
      fill="var(--rf-accent)"
    />
  </svg>
);

export const CarpetIllustration = ({ className }) => (
  <svg
    viewBox="0 0 96 96"
    className={className}
    role="img"
    aria-hidden="true"
    focusable="false"
    fill="none"
  >
    <rect x="16" y="24" width="64" height="44" rx="6" stroke="currentColor" strokeWidth="2.4" />
    <rect x="24" y="32" width="48" height="28" rx="3" stroke="currentColor" strokeWidth="1.5" opacity="0.55" />
    <path
      d="M30 39h36M30 46h36M30 53h36"
      stroke="currentColor"
      strokeWidth="1.3"
      strokeLinecap="round"
      opacity="0.4"
    />
    <path
      d="M18 68v6M26 68v6M34 68v6M42 68v6M50 68v6M58 68v6M66 68v6M74 68v6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      opacity="0.5"
    />
    <path
      d="M70 15l1.6 3.9 3.9 1.6-3.9 1.6L70 27.6l-1.6-3.9-3.9-1.6 3.9-1.6L70 15z"
      fill="var(--rf-accent)"
    />
  </svg>
);

// Photo assets (generated with Nano Banana) live in `public/raffle/`.
// `image` is optional: if the file is missing the card gracefully falls back to
// the line-art `Illustration` above (see PrizeCard's onError handler).
const asset = (name) => `${process.env.PUBLIC_URL || ''}/raffle/${name}`;

// Celebration image shown in the winner modal (optional). Falls back to the
// won prize's image, then to nothing, if the file is missing.
export const WINNER_IMAGE = asset('winner.webp');

export const PRIZES = [
  {
    id: 'apartment',
    titleKey: 'prizes.apartmentTitle',
    descKey: 'prizes.apartmentDesc',
    Illustration: ApartmentIllustration,
    image: asset('prize-apartment.jpg'),
    priceMdl: 1200,
    featured: true,
  },
  {
    id: 'checkup',
    titleKey: 'prizes.checkupTitle',
    descKey: 'prizes.checkupDesc',
    Illustration: CheckupIllustration,
    image: asset('prize-checkup.jpg'),
    priceMdl: 1000,
  },
  {
    id: 'sofa',
    titleKey: 'prizes.sofaTitle',
    descKey: 'prizes.sofaDesc',
    Illustration: SofaIllustration,
    image: asset('prize-sofa.jpg'),
    priceMdl: 900,
  },
  {
    id: 'carpet',
    titleKey: 'prizes.carpetTitle',
    descKey: 'prizes.carpetDesc',
    Illustration: CarpetIllustration,
    image: asset('prize-carpet.jpg'),
    priceMdl: 800,
  },
];

export const getPrizeById = (id) => PRIZES.find((p) => p.id === id) || PRIZES[0];

export default PRIZES;
